import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  LoadingSpinner,
  MultiselectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterLitigationDatePlan,
  useDeleteMatterLitigationDatePlan,
  useUpdateMatterLitigationDatePlan,
} from "@/lib/data/react-query/matters/matter-litigation-date-plan";
import { MatterLitigationDatePlanSchema } from "@/lib/data/schemas";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterLitigationDatePlan } from "@/lib/types";
import { US_STATES } from "@/lib/utils/states";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditMatterLitigationDatePlanModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onTransitionToEdit?: (plan: MatterLitigationDatePlan) => void;
}

interface ConditionalProps {
  editing?: boolean;
  activeMatterLitigationDatePlan?: MatterLitigationDatePlan;
  isReadOnly?: boolean;
}

const formSchema = MatterLitigationDatePlanSchema.pick({
  label: true,
  description: true,
  incidentState: true,
}).extend({
  description: z
    .string()
    .max(5000, { message: "Description must be 5,000 characters or less" })
    .optional(),
});

type FormValues = z.infer<typeof formSchema>;

const defaultFormValues: FormValues = {
  label: "",
  incidentState: [],
  description: "",
};

const sanitizePayload = (values: FormValues) => ({
  label: values.label.trim(),
  incidentState: values.incidentState,
  description: values.description?.trim() || undefined,
});

const CREATE_FORM_ID = "create-matter-litigation-plan-form";
const EDIT_FORM_ID = "edit-matter-litigation-plan-form";

export const AddOrEditMatterLitigationDatePlanModal = ({
  open,
  setOpen,
  editing,
  activeMatterLitigationDatePlan,
  isReadOnly,
  onTransitionToEdit,
}: AddOrEditMatterLitigationDatePlanModalProps & ConditionalProps) => {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);

  const formValues = useMemo(() => {
    if (editing && activeMatterLitigationDatePlan) {
      return {
        label: activeMatterLitigationDatePlan.label,
        incidentState: activeMatterLitigationDatePlan.incidentState,
        description: activeMatterLitigationDatePlan.description ?? "",
      } satisfies FormValues;
    }

    return defaultFormValues;
  }, [editing, activeMatterLitigationDatePlan]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: formValues,
    mode: "onSubmit",
  });

  const createMatterLitigationDatePlan = useCreateMatterLitigationDatePlan();
  const updateMatterLitigationDatePlan = useUpdateMatterLitigationDatePlan();
  const deleteMatterLitigationDatePlan = useDeleteMatterLitigationDatePlan();

  const isFormLoading =
    createMatterLitigationDatePlan.isPending ||
    updateMatterLitigationDatePlan.isPending ||
    deleteMatterLitigationDatePlan.isPending;

  const formId = editing ? EDIT_FORM_ID : CREATE_FORM_ID;
  const hasDirtyFields = Object.keys(form.formState.dirtyFields).length > 0;
  const isFormDirty = form.formState.isDirty || hasDirtyFields;

  useEffect(() => {
    if (!open) {
      form.reset(formValues);
      setIsDeleting(false);
    }
  }, [open, form, formValues]);

  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDatePlan,
    ACTION_PERMISSION_VALUES.delete,
  );

  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDatePlan,
    ACTION_PERMISSION_VALUES.update,
  );

  const handleSubmit = async (values: FormValues) => {
    const payload = sanitizePayload(values);

    if (editing && activeMatterLitigationDatePlan) {
      if (!hasUpdatePermission) {
        toast.error(
          "You do not have permission to update this litigation date plan",
        );
        return;
      }

      await updateMatterLitigationDatePlan.mutateAsync({
        id: activeMatterLitigationDatePlan.id,
        dateModified: activeMatterLitigationDatePlan.dateModified,
        ...payload,
      });

      setOpen(false);
      return;
    }

    const { data: createdPlan } =
      await createMatterLitigationDatePlan.mutateAsync(payload);

    onTransitionToEdit?.(createdPlan);
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!activeMatterLitigationDatePlan) return;

    setIsDeleting(false);
    setOpen(false);

    await deleteMatterLitigationDatePlan.mutateAsync({
      id: activeMatterLitigationDatePlan.id,
    });
  };

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={editing ? "Edit litigation plan" : "Add litigation plan"}
          description={
            editing
              ? "Update the litigation plan details."
              : "Create a litigation plan template for your matters."
          }
          size="2xl"
          loading={isFormLoading}
          isDirty={isFormDirty}
          footer={
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
              {!isReadOnly && editing && hasDeletePermission ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="sm:mr-auto"
                  onClick={() => {
                    setIsDeleting(true);
                  }}
                >
                  Delete
                </Button>
              ) : (
                <div className="hidden sm:block sm:flex-1" />
              )}
              <div className="flex justify-end gap-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={isFormDirty}
                />
                {!isReadOnly ? (
                  <Button
                    type="submit"
                    form={formId}
                    disabled={isFormLoading || !isFormDirty}
                  >
                    {isFormLoading ? <LoadingSpinner /> : "Save"}
                  </Button>
                ) : null}
              </div>
            </div>
          }
        >
          <form id={formId} onSubmit={form.handleSubmit(handleSubmit)}>
            <fieldset
              disabled={isReadOnly}
              className="grid gap-6 sm:grid-cols-2"
            >
              <div className="sm:col-span-2">
                <TextFormField
                  label="Name*"
                  placeholder="Enter litigation plan name"
                  register={form.register("label")}
                />
              </div>

              <div className="sm:col-span-2">
                <TextAreaFormField
                  label="Description"
                  register={form.register("description")}
                />
              </div>

              <MultiselectFormField
                label="State"
                placeholder="Select one or more states"
                options={US_STATES.map((state) => ({
                  label: state,
                  value: state,
                }))}
                register={form.register("incidentState")}
              />
            </fieldset>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this litigation date plan?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
};
